import React from "react";
import { Box, Typography, Select, MenuItem, FormControl, TextField } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";

const STATE_OPTIONS = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra",
  "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
].map((s) => ({ value: s.replace(/\s/g, ""), label: s }));

const selectSx = {
  borderRadius: "9999px",
  backgroundColor: "rgba(183, 191, 217, 0.1)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(33, 34, 38, 0.06)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(33, 34, 38, 0.12)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#2563eb",
  },
  "& .MuiSelect-select": {
    py: 1,
    px: 2,
    fontSize: "13px",
    fontWeight: 500,
    color: "#121317",
  },
};

const dateInputSx = {
  ...selectSx,
  "& .MuiInputBase-input": {
    py: 1,
    px: 2,
    fontSize: "13px",
    fontWeight: 500,
    color: "#121317",
  },
  "& .MuiInputBase-input::-webkit-calendar-picker-indicator": {
    cursor: "pointer",
  },
};



/** Build category dropdown options: API returns [{ _id, name }, ...] */
function buildCategoryOptions(categoryOptions = []) {
  const all = { value: "", label: "All" };
  const fromApi = (categoryOptions || []).map((c) => ({
    value: c._id ?? c.id ?? "",
    label: c.name ?? c.label ?? String(c._id ?? c.id ?? ""),
  })).filter((opt) => opt.value);
  return [all, ...fromApi];
}

export default function MarketplaceFilters({
  category,
  setCategory,
  categoryOptions = [],
  selectedCompany,
  handleCompanyChange,
  AllCompanyName,
  priceRevers,
  setPriceRevers,
  productAnalysis,
  setProductAnalysis,
  state,
  handleStateChanges,
  date,
  setDate,
  startingDate,
  setStartingDate,
  endingDate,
  setEndingDate,
  productStatus,
  setProductStatus,
  hideProductStatusFilter = false,
  forcedProductStatus = "",
  showPendingQueueStatusFilter = false,
  showDelistedStatusFilter = false,
  pendingQueueStatus = "",
  setPendingQueueStatus = () => {},
  searchQuery = "",
  setSearchQuery = () => {},
}) {
  const categorySelectOptions = buildCategoryOptions(categoryOptions);
  const normalizedForcedStatus = String(forcedProductStatus || "").toLowerCase();
  const statusOptions = [
    { value: "", label: "All" },
    { value: "Approved", label: "Approved" },
    { value: "pendingapproval", label: "Pending Approval" },
    { value: "productinformation", label: "Draft" },
    { value: "Delist", label: "Delisted" },
    { value: "Review", label: "In Review" },
    { value: "Rejected", label: "Rejected" },
  ].filter((opt) => {
    if (showDelistedStatusFilter) {
      return opt.value === "" || opt.value === "Delist" || opt.value === "Rejected";
    }
    if (normalizedForcedStatus === "approved") {
      return opt.value === "" || opt.value === "Approved" ;
    }
    if (normalizedForcedStatus === "pendingapproval") {
      return opt.value !== "Approved" && opt.value !== "Delist";
    }
    return true;
  });
  const pendingQueueStatusOptions = [
    { value: "", label: "All" },
    { value: "pendingapproval", label: "Pending Approval" },
    { value: "indraft", label: "In Draft" },
    { value: "Review", label: "In Review" },
  ];

  return (
    <Box className="marketplace-filters glass-container">
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, width: "100%" }}>
        <Box className="filter-item">
          <Typography className="filter-label">Search</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by product, company, or ID"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={dateInputSx}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "rgba(100,116,139,0.8)", mr: 1, fontSize: 18 }} />,
            }}
          />
        </Box>
        <Box className="filter-item">
          <Typography className="filter-label">Category</Typography>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              sx={selectSx}
            >
              {categorySelectOptions.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value} sx={{ fontSize: "13px" }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box className="filter-item">
          <Typography className="filter-label">Company</Typography>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={selectedCompany}
              onChange={handleCompanyChange}
              IconComponent={KeyboardArrowDownIcon}
              sx={selectSx}
            >
              <MenuItem value="" sx={{ fontSize: "13px" }}>All Companies</MenuItem>
              {AllCompanyName?.map((name, index) => (
                <MenuItem key={index} value={name?.companyName} sx={{ fontSize: "13px" }}>
                  {name?.companyName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box className="filter-item">
          <Typography className="filter-label">Price Order</Typography>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={priceRevers}
              onChange={(e) => setPriceRevers(e.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              sx={selectSx}
            >
              <MenuItem value="" sx={{ fontSize: "13px" }}>All</MenuItem>
              <MenuItem value="LowToHigh" sx={{ fontSize: "13px" }}>Low to High</MenuItem>
              <MenuItem value="HighToLow" sx={{ fontSize: "13px" }}>High to Low</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box className="filter-item">
          <Typography className="filter-label">Analysis Type</Typography>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={productAnalysis}
              onChange={(e) => setProductAnalysis(e.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              sx={selectSx}
            >
              <MenuItem value="" sx={{ fontSize: "13px" }}>All</MenuItem>
              <MenuItem value="MostViewed" sx={{ fontSize: "13px" }}>Most Viewed</MenuItem>
              <MenuItem value="MostAddToCard" sx={{ fontSize: "13px" }}>Most Added to Cart</MenuItem>
              <MenuItem value="MostBuyed" sx={{ fontSize: "13px" }}>Most Bought</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box className="filter-item">
          <Typography className="filter-label">Location</Typography>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={state}
              onChange={(e) => handleStateChanges(e.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              sx={selectSx}
            >
              <MenuItem value="" sx={{ fontSize: "13px" }}>All</MenuItem>
              {STATE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px" }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box className="filter-item">
          <Typography className="filter-label">Time Period</Typography>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setStartingDate("");
                setEndingDate("");
              }}
              IconComponent={KeyboardArrowDownIcon}
              sx={selectSx}
            >
              <MenuItem value="" sx={{ fontSize: "13px" }}>All Time</MenuItem>
              <MenuItem value="7" sx={{ fontSize: "13px" }}>Last 7 Days</MenuItem>
              <MenuItem value="15" sx={{ fontSize: "13px" }}>Last 15 Days</MenuItem>
              <MenuItem value="30" sx={{ fontSize: "13px" }}>1 Month</MenuItem>
              <MenuItem value="90" sx={{ fontSize: "13px" }}>3 Months</MenuItem>
              <MenuItem value="180" sx={{ fontSize: "13px" }}>6 Months</MenuItem>
              <MenuItem value="360" sx={{ fontSize: "13px" }}>1 Year</MenuItem>
              <MenuItem value="1080" sx={{ fontSize: "13px" }}>3 Years</MenuItem>
              <MenuItem value="1800" sx={{ fontSize: "13px" }}>5 Years</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box className="filter-item">
          <Typography className="filter-label">From Date</Typography>
          <TextField
            fullWidth
            size="small"
            type="date"
            value={startingDate}
            onChange={(e) => {
              setStartingDate(e.target.value);
              setDate("");
            }}
            inputProps={{ max: endingDate || undefined }}
            sx={dateInputSx}
          />
        </Box>
        <Box className="filter-item">
          <Typography className="filter-label">To Date</Typography>
          <TextField
            fullWidth
            size="small"
            type="date"
            value={endingDate}
            onChange={(e) => {
              setEndingDate(e.target.value);
              setDate("");
            }}
            inputProps={{ min: startingDate || undefined }}
            sx={dateInputSx}
          />
        </Box>
        {showPendingQueueStatusFilter && (
          <Box className="filter-item">
            <Typography className="filter-label">Status</Typography>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={pendingQueueStatus}
                onChange={(e) => setPendingQueueStatus(e.target.value)}
                IconComponent={KeyboardArrowDownIcon}
                sx={selectSx}
              >
                {pendingQueueStatusOptions.map((opt) => (
                  <MenuItem key={opt.value || "all"} value={opt.value} sx={{ fontSize: "13px" }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {!hideProductStatusFilter && !showPendingQueueStatusFilter && (
          <Box className="filter-item">
            <Typography className="filter-label">Status</Typography>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={productStatus}
                onChange={(e) => setProductStatus(e.target.value)}
                IconComponent={KeyboardArrowDownIcon}
                sx={selectSx}
              >
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value || "all"} value={opt.value} sx={{ fontSize: "13px" }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
    </Box>
  );
}
